'use client';

import { Suspense, use } from 'react';

import type { CmsData, CmsDataJsonApi } from '@packages/cms-components/interfaces';
import { LazyRender } from '@packages/modules/src/LazyRender/LazyRender';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { CategoryPageDefaultContentFragmentFragmentDoc } from '@packages/gql/generated/shopping/CategoryPageDefaultContentFragmentFragmentDoc';
import type {
  CategoryPageDefaultContentFragmentFragment,
  NavigationForCategoryDataQuery,
} from '@packages/gql/generated/shopping/graphql';
import type { ProductListSeoFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductListSeoFragmentFragmentDoc';
import { ContentExternal } from '@packages/cms-components/src/modules/ContentExternal/ContentExternal';
import { LastSeenProductsRecoSlider } from '@packages/recommendations/src/components/ProductsRecoSliders/LastSeenProductsRecoSlider/LastSeenProductsRecoSlider.client';
import type { Device } from '@packages/shared/src/utils/types';

import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';
import { defineMessages, useIntl } from 'react-intl';
import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';
import { ProductListPageLayout } from '../ProductListPageLayout/ProductListPageLayout.client';
import { ProductListBreadcrumbs } from '../ProductListBreadcrumbs/ProductListBreadcrumbs';
import { ProductListSeo } from '../ProductListSeo/ProductListSeo';
import { ProductCategoryNavigationDesktop } from '../ProductCategoryNavigationDesktop/ProductCategoryNavigationDesktop.client';
import { SeoProductCategoryNavigation } from '../SeoProductCategoryNavigation/SeoProductCategoryNavigation';
import { ProductCategoryNavigationMobile } from '../ProductCategoryNavigationMobile/ProductCategoryNavigationMobile';
import { ProductCategoryNavigationMobileV2 } from '../ProductCategoryNavigationMobileV2/ProductCategoryNavigationMobileV2';
import { CmsTopBanner } from '../CmsTopBanner/CmsTopBanner';
import { useDisplayMode } from '../../hooks/useDisplayMode';
import { bypassProductListPageLayoutMessages } from '../../bypassBlocks.messages';
import { DisplayCategoryTracking } from '../DisplayCategoryTracking/DisplayCategoryTracking';
import { SearchKimRatingDynamic } from '../SearchKimRating/SearchKimRating.dynamic';
import { useFeatureFlag } from '../../utils/featureFlags/useFeatureFlag';
import { filtersV2 } from '../../activeFeatureFlags';

/* GraphQL */ `
  fragment CategoryPageDefaultContentFragment on CategoryResult {
    ...ProductListPageLayoutFragment
    ...ProductListBreadcrumbsFragment
    ...ProductCategoryNavigationMobileWorkaroundFragment
    ...ProductCategoryNavigationMobileWorkaroundV2Fragment
    ...SearchKimRatingFragment
    ...SearchKimRatingCategoryPageFragment
    ...UseDisplayModeFragment
    ...UseDisplayCategoryTrackingFragment

    seoCategoryNavigation {
      ...SeoProductCategoryNavigationFragment
    }

    cmsContentPosition
  }
`;
const messages = defineMessages({
  leftNavigationCategoryLabel: {
    id: 'search.sidebar.navigation.categories.label',
    defaultMessage: 'Kategorienavigation',
  },
});

type NavigationItems = NavigationForCategoryDataQuery['navigationForCategory']['categories'];

// null means the navigation request failed: render nothing rather than an
// endless skeleton. An empty array is a legitimate result and renders the nav.
const DesktopNavigationItems = ({ promise }: { promise: Promise<NavigationItems | null> }) => {
  const items = use(promise);

  return items == null ? null : <ProductCategoryNavigationDesktop maskedItems={items} />;
};

const MobileNavigationItems = ({
  promise,
  result,
  onNavigate,
  isFiltersV2Enabled,
}: {
  promise: Promise<NavigationItems | null>;
  result: CategoryPageDefaultContentFragmentFragment;
  onNavigate: () => void;
  isFiltersV2Enabled: boolean;
}) => {
  const items = use(promise);

  if (items == null) {
    return null;
  }

  if (isFiltersV2Enabled) {
    return (
      <ProductCategoryNavigationMobileV2
        maskedItems={items}
        maskedResult={result}
        onNavigate={onNavigate}
      />
    );
  }

  return (
    <ProductCategoryNavigationMobile
      maskedItems={items}
      maskedResult={result}
      onNavigate={onNavigate}
    />
  );
};

export type CategoryPageDefaultContentProps = {
  maskedData: FragmentType<typeof CategoryPageDefaultContentFragmentFragmentDoc>;
  topLevelCategories?: string[];
  bannerData?: CmsDataJsonApi;
  cmsContent?: CmsData;
  maskedSeoResponse?: FragmentType<typeof ProductListSeoFragmentFragmentDoc>;
  device: Device;
  referrer?: string;
  navigationItemsPromise: Promise<NavigationItems | null>;
};

/**
 * CategoryPageDefaultContent component description displayed in storybook
 * */
export const CategoryPageDefaultContent = ({
  maskedData,
  topLevelCategories,
  bannerData,
  cmsContent,
  maskedSeoResponse,
  device,
  referrer,
  navigationItemsPromise,
}: CategoryPageDefaultContentProps) => {
  const data = unmask<typeof CategoryPageDefaultContentFragmentFragmentDoc>(maskedData);
  const { formatMessage } = useIntl();
  const isFiltersV2Enabled = useFeatureFlag(filtersV2);

  const { displayMode } = useDisplayMode(data);

  const { cmsContentPosition } = data;

  const renderMobileNavigation = (closeDialog: () => void) => (
    <Suspense fallback={<Skeleton className="ml-4 h-3 w-3/4" />}>
      <MobileNavigationItems
        promise={navigationItemsPromise}
        result={data}
        onNavigate={closeDialog}
        isFiltersV2Enabled={isFiltersV2Enabled}
      />
    </Suspense>
  );

  return (
    <>
      <ErrorBoundary>
        <DisplayCategoryTracking maskedResult={data} />
      </ErrorBoundary>

      <ProductListPageLayout
        leftSidebarAriaLabel={formatMessage(messages.leftNavigationCategoryLabel)}
        maskedData={data}
        topLevelCategories={topLevelCategories}
        device={device}
        referrer={referrer}
        enablePRankTracking
        aboveMainContentComponent={
          <>
            <ProductListBreadcrumbs maskedData={data} />
            {bannerData && cmsContentPosition !== 'TOP' && <CmsTopBanner bannerData={bannerData} />}
            {cmsContent && cmsContentPosition === 'TOP' && (
              <LazyRender fallback={<Skeleton className="h-[200px] w-full" />}>
                <ErrorBoundary>
                  <ContentExternal data={cmsContent} />
                </ErrorBoundary>
              </LazyRender>
            )}
          </>
        }
        belowProductsComponent={
          <>
            {cmsContent && cmsContentPosition === 'BOTTOM' && <ContentExternal data={cmsContent} />}

            <ErrorBoundary>
              <LastSeenProductsRecoSlider fetchOnThreshold title="" />
            </ErrorBoundary>

            <ErrorBoundary>
              <SearchKimRatingDynamic
                maskedData={data}
                maskedCategoryPageData={data}
                searchPageType="MBA"
                displayMode={displayMode}
                templateParams={{ x17: referrer ?? 'x' }}
              />
            </ErrorBoundary>

            {maskedSeoResponse && <ProductListSeo maskedData={maskedSeoResponse} />}
          </>
        }
        leftSidebarDesktopComponent={
          <div className="flex flex-col gap-1">
            <BypassBlock
              buttonText={formatMessage(bypassProductListPageLayoutMessages.categoryNavigation)}
              skipScrollIntoView
            >
              <Suspense
                fallback={
                  <div className="flex flex-col gap-1">
                    <Skeleton className="h-2 w-full" />
                    <Skeleton className="ml-1 w-3/4" />
                    <Skeleton className="ml-1 w-3/4" />
                  </div>
                }
              >
                <DesktopNavigationItems promise={navigationItemsPromise} />
              </Suspense>
              {data.seoCategoryNavigation && (
                <>
                  <hr className="opacity-15" />
                  <SeoProductCategoryNavigation maskedNavigation={data.seoCategoryNavigation} />
                </>
              )}
            </BypassBlock>
          </div>
        }
        renderCategoryDialogContent={renderMobileNavigation}
      />
    </>
  );
};
